import { useMemo } from "react";
import PropTypes from "prop-types";
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftAvatar from "components/SoftAvatar";
import SoftTypography from "components/SoftTypography";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";

function Table({ columns, rows, onRowClick }) {
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  const renderColumns = columns.map(({ name, desc, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0 || key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {desc ? desc.toUpperCase() : name.toUpperCase()}
      </SoftBox>
    );
  });

  const renderRows = rows.map((row, key) => {
    const rowKey = ["string", "number"].includes(typeof row.id) ? row.id : `row-${key}`;
    const isClickable = Boolean(onRowClick);

    const tableRow = columns.map(({ name, align }, columnIndex) => {
      let template;
      const isEdgeColumn = columnIndex === 0 || columnIndex === columns.length - 1;
      const pl = isEdgeColumn ? 3 : 1;
      const pr = isEdgeColumn ? 3 : 1;
      const justifyContent = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
      const contentAlignment = {
        display: "block",
        width: "fit-content",
        maxWidth: "100%",
        textAlign: align,
        ...(align === "center" ? { mx: "auto" } : {}),
        ...(align === "right" ? { ml: "auto" } : {}),
        ...(align === "left" ? { mr: "auto" } : {}),
      };

      if (Array.isArray(row[name])) {
        template = (
          <SoftBox
            key={`${rowKey}-${name}`}
            component="td"
            pt={1}
            pb={1}
            pl={align === "left" ? pl : 3}
            pr={align === "right" ? pr : 3}
            sx={{ verticalAlign: "middle" }}
            borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
          >
            <SoftBox display="flex" alignItems="center" justifyContent={justifyContent} py={0.5}>
              <SoftBox mr={2}>
                <SoftAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="sm" />
              </SoftBox>
              <SoftTypography variant="button" fontWeight="medium" sx={contentAlignment}>
                {row[name][1]}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        );
      } else {
        template = (
          <SoftBox
            key={`${rowKey}-${name}`}
            component="td"
            pt={1}
            pb={1}
            pl={align === "left" ? pl : 3}
            pr={align === "right" ? pr : 3}
            textAlign={align}
            sx={{ verticalAlign: "middle" }}
            borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
          >
            <SoftTypography
              variant="button"
              fontWeight="regular"
              color="secondary"
              sx={
                row[name]?.props?.image
                  ? { ...contentAlignment, verticalAlign: "middle" }
                  : contentAlignment
              }
            >
              {row[name]}
            </SoftTypography>
          </SoftBox>
        );
      }

      return template;
    });

    return (
      <TableRow
        key={rowKey}
        hover
        onClick={isClickable ? () => onRowClick(row, key) : undefined}
        sx={isClickable ? { cursor: "pointer" } : undefined}
      >
        {tableRow}
      </TableRow>
    );
  });

  return useMemo(
    () => (
      <MuiTable>
        <SoftBox component="thead">
          <TableRow>{renderColumns}</TableRow>
        </SoftBox>
        <TableBody>{renderRows}</TableBody>
      </MuiTable>
    ),
    [columns, rows, onRowClick]
  );
}

Table.defaultProps = {
  columns: [],
  rows: [{}],
  onRowClick: null,
};

Table.propTypes = {
  desc: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  onRowClick: PropTypes.func,
};

export default Table;
